import type { Faro } from '@grafana/faro-web-sdk';
import { getWebInstrumentations, initializeFaro, InternalLoggerLevel } from '@grafana/faro-web-sdk';
import { TracingInstrumentation } from '@grafana/faro-web-tracing';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request';
import { isTestEnv } from './utils';
import { addLogContext } from './addLogContext';
import { getFaroBaseUrl, IGNORED_URLS, type FaroEnvironmentVars } from './faro/config';
import { initializeTracing } from './tracing';

let faro: Faro | null = null;

const getFaro = (env: FaroEnvironmentVars): Faro | null => {
  // Note that getFaro is a singleton: once initialized, it always returns the same instance.
  // This is important because Faro is designed as a singleton, and re-initializing it
  // would cause all sorts of problems, including duplicate instrumentation, duplicated
  // logs/events/traces, etc.
  // So we always return the existing instance if it exists.  Changes to environment variables
  // after the first call to getFaro() are ignored.
  if (faro) return faro;

  if (!env.faroEnabled) return null;

  const alloyUrl = `${getFaroBaseUrl()}/alloy`;

  const { version: appVersion, faroTracingEnabled, faroPerfInstrumentationEnabled } = env;
  const webInstrumentations = getWebInstrumentations({
    enablePerformanceInstrumentation: faroPerfInstrumentationEnabled,
  });

  faro = initializeFaro({
    url: alloyUrl,
    app: {
      name: 'onebrief',
      version: appVersion,
      environment: process.env.NODE_ENV ?? 'development',
    },
    ignoreUrls: IGNORED_URLS,
    batching: {
      enabled: true,
    },
    beforeSend: addLogContext,
    // Console instrumentation moved to root config in Faro v2
    consoleInstrumentation: {
      disabledLevels: [], // Capture all console levels
    },
    instrumentations: [
      ...webInstrumentations,
      ...(faroTracingEnabled
        ? [
            new TracingInstrumentation({
              resourceAttributes: {
                'source-tracer': 'grafana-faro',
              },
              instrumentations: [
                new FetchInstrumentation({ ignoreUrls: IGNORED_URLS }),
                new XMLHttpRequestInstrumentation({ ignoreUrls: IGNORED_URLS }),
              ],
            }),
          ]
        : []),
    ],
    // Avoid global Faro re-use, to isolate instance in test env:
    isolate: isTestEnv,
    preventGlobalExposure: isTestEnv,
    // NOTE: InternalLoggerLevel.VERBOSE is useful for debugging Faro-related issues.
    internalLoggerLevel: isTestEnv ? InternalLoggerLevel.WARN : InternalLoggerLevel.ERROR,
  });

  faro?.api.pushLog(['Faro was initialized for client']);

  initializeTracing(faro);

  return faro;
};

type GetFaroFnType = typeof getFaro;
export type { GetFaroFnType, FaroEnvironmentVars };

export { getFaro };
